import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './HierarchyView.css';
import logger from './utils/logger';

const ROLE_ORDER = ['admin', 'dev', 'chief_major', 'representative', 'major_branch', 'branch', 'jisa', 'distributor', 'agency'];
const ROLE_LABELS = {
  super: '슈퍼어드민',
  admin: '본사',
  dev: '개발',
  chief_major: '총대본',
  representative: '대표자',
  major_branch: '대본사',
  branch: '부본사',
  jisa: '지사',
  distributor: '총판',
  agency: '매장'
};

const FEE_KEY_BY_ROLE = {
  admin: { deposit: 'deposit_fee_per_count', withdrawal: 'withdrawal_fee_per_count' },
  dev: { deposit: 'deposit_fee_per_count', withdrawal: 'withdrawal_fee_per_count' },
  chief_major: { deposit: 'deposit_fee_per_count_major_branch', withdrawal: 'withdrawal_fee_per_count_major_branch' },
  representative: { deposit: 'deposit_fee_per_count_branch', withdrawal: 'withdrawal_fee_per_count_branch' },
  major_branch: { deposit: 'deposit_fee_per_count_major_branch', withdrawal: 'withdrawal_fee_per_count_major_branch' },
  branch: { deposit: 'deposit_fee_per_count_branch', withdrawal: 'withdrawal_fee_per_count_branch' },
  jisa: { deposit: 'deposit_fee_per_count_jisa', withdrawal: 'withdrawal_fee_per_count_jisa' },
  distributor: { deposit: 'deposit_fee_per_count_distributor', withdrawal: 'withdrawal_fee_per_count_distributor' },
  agency: { deposit: 'deposit_fee_per_count_agency', withdrawal: 'withdrawal_fee_per_count_agency' }
};

function buildTree(users, rootUserId) {
  const byId = new Map(users.map((u) => [u.id, { ...u, children: [] }]));
  const roots = [];
  for (const u of users) {
    const node = byId.get(u.id);
    if (!node) continue;
    const parent = byId.get(node.parent_user_id);
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  if (rootUserId != null && roots.length > 1) {
    const rootNode = byId.get(Number(rootUserId));
    if (rootNode) {
      return [rootNode];
    }
  }
  roots.sort((a, b) => {
    const ai = ROLE_ORDER.indexOf(a.role);
    const bi = ROLE_ORDER.indexOf(b.role);
    if (ai !== bi) return ai - bi;
    return (a.company_name || a.username || '').localeCompare(b.company_name || b.username || '');
  });
  roots.forEach((r) => r.children.sort((a, b) => {
    const ai = ROLE_ORDER.indexOf(a.role);
    const bi = ROLE_ORDER.indexOf(b.role);
    if (ai !== bi) return ai - bi;
    return (a.company_name || a.username || '').localeCompare(b.company_name || b.username || '');
  }));
  return roots;
}

function hasMatchingDescendant(node, roleFilter) {
  if (node.role === roleFilter) return true;
  return (node.children || []).some((c) => hasMatchingDescendant(c, roleFilter));
}

function getVisibleChildren(node, roleFilter) {
  if (!roleFilter || roleFilter === 'all') return node.children || [];
  return (node.children || []).filter((c) => c.role === roleFilter || hasMatchingDescendant(c, roleFilter));
}

function TreeNode({ node, depth, expanded, onToggle, onSelect, selectedId, roleFilter, searchLower }) {
  const label = node.company_name || node.company || node.username || '';
  const code = `#${String(node.id).padStart(3, '0')}`;
  const matchesSearch = !searchLower || label.toLowerCase().includes(searchLower) || String(node.username || '').toLowerCase().includes(searchLower) || code.toLowerCase().includes(searchLower);
  const visibleChildren = getVisibleChildren(node, roleFilter);
  const hasVisibleChildren = visibleChildren.length > 0;
  const matchesRole = !roleFilter || roleFilter === 'all' || node.role === roleFilter || hasVisibleChildren;
  const isExpanded = expanded.has(node.id);
  const directChildCount = (node.children || []).length;

  if (!matchesRole && roleFilter && roleFilter !== 'all') return null;

  const descendantsMatchSearch = visibleChildren.some((c) => {
    const check = (n) => {
      const cl = (n.company_name || n.company || n.username || '').toLowerCase();
      const cd = `#${String(n.id).padStart(3, '0')}`.toLowerCase();
      if (cl.includes(searchLower) || cd.includes(searchLower)) return true;
      return (n.children || []).some(check);
    };
    return check(c);
  });
  if (searchLower && !matchesSearch && !descendantsMatchSearch) return null;

  const isSelected = selectedId === node.id;

  return (
    <div className="hierarchy-node" style={{ marginLeft: depth * 16 }}>
      <div
        className={`hierarchy-row ${isExpanded && hasVisibleChildren ? 'expanded' : ''} ${isSelected ? 'selected' : ''}`}
        onClick={() => onSelect && onSelect(node)}
      >
        <span
          className={`hierarchy-expand ${hasVisibleChildren ? 'hierarchy-expand--clickable' : ''}`}
          role={hasVisibleChildren ? 'button' : undefined}
          tabIndex={hasVisibleChildren ? 0 : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (hasVisibleChildren && onToggle) onToggle(node.id);
          }}
          onKeyDown={hasVisibleChildren ? (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && onToggle) {
              e.preventDefault();
              e.stopPropagation();
              onToggle(node.id);
            }
          } : undefined}
        >
          {hasVisibleChildren ? (isExpanded ? '▼' : '▶') : '　'}
        </span>
        <span className={`hierarchy-role role-badge ${node.role}`}>{ROLE_LABELS[node.role] || node.role}</span>
        <span className="hierarchy-name">{label || node.username || '(이름 없음)'}</span>
        {directChildCount > 0 && <span className="hierarchy-desc-count">하위 {directChildCount}개</span>}
        <span className="hierarchy-code">{code}</span>
      </div>
      {hasVisibleChildren && isExpanded && (
        <div className="hierarchy-children">
          {visibleChildren.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              selectedId={selectedId}
              roleFilter={roleFilter}
              searchLower={searchLower}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDateRangePreset(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (preset === 'daily') {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return { start: toDateStr(yesterday), end: toDateStr(yesterday) };
  }
  if (preset === 'weekly') {
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 6);
    return { start: toDateStr(weekAgo), end: toDateStr(today) };
  }
  if (preset === 'monthly') {
    const monthAgo = new Date(today);
    monthAgo.setDate(monthAgo.getDate() - 29);
    return { start: toDateStr(monthAgo), end: toDateStr(today) };
  }
  return null;
}

function HierarchyView() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [expanded, setExpanded] = useState(new Set());
  const [selectedUser, setSelectedUser] = useState(null);
  const [settlementSummary, setSettlementSummary] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementDateRange, setSettlementDateRange] = useState(() => getDateRangePreset('monthly'));
  const [dateFilterPreset, setDateFilterPreset] = useState('monthly');
  const [settlementTab, setSettlementTab] = useState('all');
  const [showRateFeeSection, setShowRateFeeSection] = useState(true);
  /** 접힌 분류 카드 키 — 미포함이면 펼침(디폴트 전체 펼침) */
  const [collapsedCompanyCards, setCollapsedCompanyCards] = useState(() => new Set());
  const UNSETTLED_PAGE_SIZE = 12;
  const [unsettledPage, setUnsettledPage] = useState(1);
  const [currentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (e) {
      return null;
    }
  });

  const fetchHierarchy = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/users/hierarchy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data || []);
      setError(null);
    } catch (err) {
      logger.apiError('GET', '/api/users/hierarchy', err);
      setError(err.response?.data?.error || '데이터를 불러오지 못했습니다.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHierarchy();
  }, []);

  const fetchSettlementSummary = React.useCallback(() => {
    if (!selectedUser?.id) return;
    setSettlementLoading(true);
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    params.set('start_date', settlementDateRange.start);
    params.set('end_date', settlementDateRange.end);
    if (settlementTab && settlementTab !== 'all') params.set('company', settlementTab);
    axios
      .get(`/api/users/${selectedUser.id}/settlement-summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => setSettlementSummary(res.data))
      .catch((err) => {
        logger.apiError('GET', `/api/users/${selectedUser.id}/settlement-summary`, err);
        setSettlementSummary(null);
      })
      .finally(() => setSettlementLoading(false));
  }, [selectedUser?.id, settlementDateRange, settlementTab]);

  useEffect(() => {
    setSettlementTab('all');
  }, [selectedUser?.id]);

  useEffect(() => {
    setUnsettledPage(1);
  }, [selectedUser?.id, settlementDateRange?.start, settlementDateRange?.end, settlementTab]);

  useEffect(() => {
    if (!selectedUser?.id) {
      setSettlementSummary(null);
      return;
    }
    fetchSettlementSummary();
  }, [selectedUser?.id, settlementDateRange.start, settlementDateRange.end, settlementTab, fetchSettlementSummary]);

  const treeRoots = useMemo(() => {
    if (currentUser?.role === 'super') {
      return buildTree(users, null);
    }
    return buildTree(users, currentUser?.id);
  }, [users, currentUser]);

  const searchLower = search.trim().toLowerCase();

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const collectIds = (nodes) => {
      let ids = [];
      for (const n of nodes) {
        if (n.children?.length) {
          ids.push(n.id);
          ids = ids.concat(collectIds(n.children));
        }
      }
      return ids;
    };
    setExpanded(new Set(collectIds(treeRoots)));
  };

  const collapseAll = () => setExpanded(new Set());

  const toggleCompanyCardCollapse = (companyKey) => {
    setCollapsedCompanyCards((prev) => {
      const next = new Set(prev);
      if (next.has(companyKey)) next.delete(companyKey);
      else next.add(companyKey);
      return next;
    });
  };

  const isCompanyCardExpanded = (companyKey) => !collapsedCompanyCards.has(companyKey);

  if (loading) {
    return (
      <div className="hierarchy-page">
        <div className="hierarchy-container">
          <div className="hierarchy-loading">사용자 데이터를 불러오는 중...</div>
        </div>
        <div className="hierarchy-detail-card">
          <h2 className="hierarchy-detail-title">선택 사용자 정보</h2>
          <div className="hierarchy-detail-empty">로딩 중...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hierarchy-page">
        <div className="hierarchy-container">
          <div className="hierarchy-error">{error}</div>
        </div>
        <div className="hierarchy-detail-card">
          <h2 className="hierarchy-detail-title">선택 사용자 정보</h2>
          <div className="hierarchy-detail-empty">계보도에서 사용자를 선택하면 정보가 표시됩니다.</div>
        </div>
      </div>
    );
  }

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      const dt = typeof d === 'string' ? new Date(d) : d;
      return Number.isNaN(dt.getTime()) ? '-' : dt.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return '-';
    }
  };

  const formatNumber = (v) => new Intl.NumberFormat('ko-KR').format(Math.floor(v || 0));
  const formatCurrency = (v) => `${formatNumber(v)}원`;

  return (
    <div className="hierarchy-page">
      <div className="hierarchy-container">
        <h1 className="hierarchy-title">사용자 계보도</h1>

        <div className="hierarchy-controls">
        <div className="hierarchy-search-wrap">
          <span className="hierarchy-search-icon">🔍</span>
          <input
            type="text"
            className="hierarchy-search"
            placeholder="코드 또는 사용자명 검색..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="hierarchy-role-filters">
          <span className="hierarchy-filter-icon">⚙</span>
          {['all', 'admin', 'dev', 'chief_major', 'representative', 'major_branch', 'branch', 'jisa', 'distributor', 'agency'].map((r) => (
            <button
              key={r}
              type="button"
              className={`hierarchy-role-btn ${roleFilter === r ? 'active' : ''}`}
              onClick={() => setRoleFilter(r)}
            >
              {r === 'all' ? '전체' : ROLE_LABELS[r] || r}
            </button>
          ))}
        </div>

        <div className="hierarchy-expand-actions">
          <button type="button" className="hierarchy-expand-btn" onClick={expandAll}>전체 펼치기</button>
          <button type="button" className="hierarchy-expand-btn" onClick={collapseAll}>전체 접기</button>
        </div>
      </div>

        <div className="hierarchy-tree">
          <div className="hierarchy-tree-inner">
            {treeRoots.length === 0 ? (
              <div className="hierarchy-empty">표시할 사용자가 없습니다.</div>
            ) : (
              treeRoots
                .map((node) => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    depth={0}
                    expanded={expanded}
                    onToggle={toggleExpand}
                    onSelect={setSelectedUser}
                    selectedId={selectedUser?.id}
                    roleFilter={roleFilter}
                    searchLower={searchLower}
                  />
                ))
                .filter(Boolean)
            )}
          </div>
        </div>
      </div>

      <div className="hierarchy-detail-card">
        <div className="hierarchy-detail-header">
          <h2 className="hierarchy-detail-title">선택 사용자</h2>
          {selectedUser && (
            <button
              type="button"
              className="hierarchy-detail-refresh"
              onClick={fetchSettlementSummary}
              title="정산 데이터 새로고침"
              disabled={settlementLoading}
            >
              {settlementLoading ? '…' : '🔄'}
            </button>
          )}
        </div>
        {selectedUser ? (
          <div className="hierarchy-detail-body">
            <div className="hierarchy-user-profile">
              <div className="hierarchy-user-profile-main">
                <span className={`hierarchy-user-role-badge ${selectedUser.role}`}>{ROLE_LABELS[selectedUser.role] || selectedUser.role}</span>
                <h3 className="hierarchy-user-profile-name">{selectedUser.company_name || selectedUser.company || selectedUser.username || '(이름 없음)'}</h3>
                <span className="hierarchy-user-profile-code">#{String(selectedUser.id).padStart(3, '0')}</span>
              </div>
            </div>
            <div className="hierarchy-detail-grid">
              <div className="hierarchy-detail-row">
                <span className="hierarchy-detail-label">사용자명</span>
                <span className="hierarchy-detail-value">{selectedUser.company_name || selectedUser.company || selectedUser.username || '-'}</span>
              </div>
              <div className="hierarchy-detail-row">
                <span className="hierarchy-detail-label">코드</span>
                <span className="hierarchy-detail-value hierarchy-detail-code">#{String(selectedUser.id).padStart(3, '0')}</span>
              </div>
              <div className="hierarchy-detail-row">
                <span className="hierarchy-detail-label">아이디</span>
                <span className="hierarchy-detail-value">{selectedUser.username || '-'}</span>
              </div>
              <div className="hierarchy-detail-row">
                <span className="hierarchy-detail-label">역할</span>
                <span className={`hierarchy-detail-value hierarchy-role role-badge ${selectedUser.role}`}>{ROLE_LABELS[selectedUser.role] || selectedUser.role}</span>
              </div>
              <div className="hierarchy-detail-row">
                <span className="hierarchy-detail-label">직계 하위</span>
                <span className="hierarchy-detail-value">
                  {(() => {
                    const children = selectedUser.children || [];
                    if (children.length === 0) return '0명';
                    const names = children.slice(0, 5).map((c) => c.company_name || c.company || c.username).filter(Boolean);
                    return `${children.length}명${names.length > 0 ? ` (${names.join(', ')}${children.length > 5 ? ' 외' : ''})` : ''}`;
                  })()}
                </span>
              </div>
              <div className="hierarchy-detail-row">
                <span className="hierarchy-detail-label">가입일</span>
                <span className="hierarchy-detail-value">{formatDate(selectedUser.created_at)}</span>
              </div>
              {selectedUser.account && (
                <div className="hierarchy-detail-row hierarchy-detail-row-full">
                  <span className="hierarchy-detail-label">계좌</span>
                  <span className="hierarchy-detail-value">{selectedUser.account}</span>
                </div>
              )}
              {(selectedUser.fee != null && selectedUser.fee !== '' && Number(selectedUser.fee) !== 0) && (
                <div className="hierarchy-detail-row hierarchy-detail-row-full">
                  <span className="hierarchy-detail-label">수수료율</span>
                  <span className="hierarchy-detail-value">{Number(selectedUser.fee)}%</span>
                </div>
              )}
            </div>

            <div className="hierarchy-detail-section">
              <h3 className="hierarchy-detail-section-title">📊 정산 대시보드</h3>
              {settlementLoading ? (
                <div className="hierarchy-detail-empty">로딩 중...</div>
              ) : settlementSummary?.companies?.length > 0 ? (
                <div className="hierarchy-dashboard">
                  <div className="hierarchy-dashboard-meta">
                    <div className="hierarchy-dashboard-date-filter">
                      <div className="hierarchy-dashboard-date-inputs">
                        <label>기간</label>
                        <input
                          type="date"
                          value={settlementDateRange.start}
                          onChange={(e) => {
                            setSettlementDateRange((p) => ({ ...p, start: e.target.value }));
                            setDateFilterPreset(null);
                          }}
                        />
                        <span className="hierarchy-dashboard-date-sep">~</span>
                        <input
                          type="date"
                          value={settlementDateRange.end}
                          onChange={(e) => {
                            setSettlementDateRange((p) => ({ ...p, end: e.target.value }));
                            setDateFilterPreset(null);
                          }}
                        />
                      </div>
                      <div className="hierarchy-dashboard-date-presets">
                        <button
                          type="button"
                          className={`hierarchy-dashboard-date-preset-btn ${dateFilterPreset === 'daily' ? 'active' : ''}`}
                          onClick={() => {
                            const range = getDateRangePreset('daily');
                            if (range) {
                              setSettlementDateRange(range);
                              setDateFilterPreset('daily');
                            }
                          }}
                        >
                          일일
                        </button>
                        <button
                          type="button"
                          className={`hierarchy-dashboard-date-preset-btn ${dateFilterPreset === 'weekly' ? 'active' : ''}`}
                          onClick={() => {
                            const range = getDateRangePreset('weekly');
                            if (range) {
                              setSettlementDateRange(range);
                              setDateFilterPreset('weekly');
                            }
                          }}
                        >
                          일주일
                        </button>
                        <button
                          type="button"
                          className={`hierarchy-dashboard-date-preset-btn ${dateFilterPreset === 'monthly' ? 'active' : ''}`}
                          onClick={() => {
                            const range = getDateRangePreset('monthly');
                            if (range) {
                              setSettlementDateRange(range);
                              setDateFilterPreset('monthly');
                            }
                          }}
                        >
                          한달
                        </button>
                      </div>
                    </div>
                    <div className="hierarchy-dashboard-tabs">
                      <button
                        type="button"
                        className={`hierarchy-dashboard-tab ${settlementTab === 'all' ? 'active' : ''}`}
                        onClick={() => setSettlementTab('all')}
                      >
                        종합
                      </button>
                      {(settlementSummary.companies || []).map((c) => (
                        <button
                          key={c}
                          type="button"
                          className={`hierarchy-dashboard-tab ${settlementTab === c ? 'active' : ''}`}
                          onClick={() => setSettlementTab(c)}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="hierarchy-dashboard-summary">
                    <div className="hierarchy-dashboard-summary-item" style={{ gridColumn: 1, gridRow: 1 }}>
                      <span className="hierarchy-dashboard-summary-label">총 입금액</span>
                      <span className="hierarchy-dashboard-summary-value">{formatCurrency(settlementSummary.periodSummary?.deposit_total ?? 0)}</span>
                    </div>
                    <div className="hierarchy-dashboard-summary-item" style={{ gridColumn: 1, gridRow: 2 }}>
                      <span className="hierarchy-dashboard-summary-label">총 정산액</span>
                      <span className="hierarchy-dashboard-summary-value">{formatCurrency(settlementSummary.periodSummary?.total_settlement ?? 0)}</span>
                    </div>
                    <div className="hierarchy-dashboard-summary-item" style={{ gridColumn: 2, gridRow: 1 }}>
                      <span className="hierarchy-dashboard-summary-label">정산 완료 금액</span>
                      <span className="hierarchy-dashboard-summary-value">{formatCurrency(settlementSummary.periodSummary?.settled_amount ?? 0)}</span>
                    </div>
                    <div className="hierarchy-dashboard-summary-item" style={{ gridColumn: 2, gridRow: 2 }}>
                      <span className="hierarchy-dashboard-summary-label">미정산 금액</span>
                      <span className="hierarchy-dashboard-summary-value hierarchy-dashboard-summary-unsettled">{formatCurrency(settlementSummary.periodSummary?.unsettled_amount ?? 0)}</span>
                    </div>
                    <div className="hierarchy-dashboard-summary-item" style={{ gridColumn: 3, gridRow: 1 }}>
                      <span className="hierarchy-dashboard-summary-label">월 이용료</span>
                      <span className="hierarchy-dashboard-summary-value">{formatCurrency(settlementSummary?.monthlyFeeTotal ?? 0)}</span>
                    </div>
                    <div className="hierarchy-dashboard-summary-item" style={{ gridColumn: 3, gridRow: 2 }}>
                      <span className="hierarchy-dashboard-summary-label">일 평균 금액</span>
                      <span className="hierarchy-dashboard-summary-value">
                        {(() => {
                          const start = settlementSummary?.startDate;
                          const end = settlementSummary?.endDate;
                          const total = Number(settlementSummary?.periodSummary?.total_settlement ?? 0);
                          if (!start || !end || total === 0) return formatCurrency(0);
                          const s = new Date(start);
                          const e = new Date(end);
                          const days = Math.max(1, Math.floor((e - s) / (24 * 60 * 60 * 1000)) + 1);
                          return formatCurrency(Math.floor(total / days));
                        })()}
                      </span>
                    </div>
                  </div>

                  <div className="hierarchy-dashboard-block hierarchy-dashboard-block-merged">
                    <div className="hierarchy-dashboard-block-header-row">
                      <h4 className="hierarchy-dashboard-block-title">분류별 요율·수수료 · 선택 기간 정산</h4>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={showRateFeeSection}
                        className={`hierarchy-dashboard-toggle ${showRateFeeSection ? 'on' : 'off'}`}
                        onClick={() => setShowRateFeeSection((p) => !p)}
                      >
                        <span className="hierarchy-dashboard-toggle-track">
                          <span className="hierarchy-dashboard-toggle-thumb" />
                        </span>
                        <span className="hierarchy-dashboard-toggle-label">{showRateFeeSection ? 'ON' : 'OFF'}</span>
                      </button>
                    </div>
                    {(() => {
                      const feeMap = {};
                      (settlementSummary.feeSettings || []).forEach((f) => {
                        feeMap[f.company || '미지정'] = f;
                      });
                      const rateByCompany = {};
                      (settlementSummary.rateSettings || []).forEach((r) => {
                        const key = r.company || '미지정';
                        if (!rateByCompany[key]) rateByCompany[key] = [];
                        rateByCompany[key].push(r);
                      });
                      const byCompany = settlementSummary.byCompany || {};
                      const allCompanies = [...new Set([...Object.keys(rateByCompany), ...Object.keys(feeMap), ...Object.keys(byCompany)])].filter(Boolean).sort();
                      const companiesToShow = settlementTab === 'all' ? allCompanies : allCompanies.filter((c) => c === settlementTab);
                      if (companiesToShow.length === 0) {
                        return <div className="hierarchy-dashboard-empty-text">설정된 요율·수수료 없음</div>;
                      }
                      const keys = FEE_KEY_BY_ROLE[selectedUser.role];
                      return (
                        <div className="hierarchy-dashboard-by-company">
                          {companiesToShow.map((company) => {
                            const periodData = byCompany[company];
                            const rates = rateByCompany[company] || [];
                            const fees = feeMap[company] && keys ? feeMap[company] : null;
                            const expandedCard = isCompanyCardExpanded(company);
                            return (
                              <div
                                key={company}
                                className="hierarchy-dashboard-company-card"
                                data-accordion-open={expandedCard ? 'true' : 'false'}
                              >
                                <button
                                  type="button"
                                  className="hierarchy-dashboard-company-card-header hierarchy-dashboard-company-card-header--toggle"
                                  onClick={() => toggleCompanyCardCollapse(company)}
                                  aria-expanded={expandedCard}
                                >
                                  <span className="hierarchy-dashboard-company-chevron" aria-hidden>{expandedCard ? '▼' : '▶'}</span>
                                  <span className="hierarchy-dashboard-company-card-title-text">{company}</span>
                                </button>
                                {expandedCard ? (
                                  <div className="hierarchy-dashboard-company-card-body">
                                    {showRateFeeSection && (
                                    <div className="hierarchy-dashboard-company-cards-row">
                                      {rates.length > 0 ? (
                                        rates.map((r, i) => (
                                          <div key={i} className="hierarchy-dashboard-company-mini-card">
                                            <span className="hierarchy-dashboard-mini-label">{r.label || '요율'}</span>
                                            <span className="hierarchy-dashboard-mini-value">{Number(r.rate)}%</span>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="hierarchy-dashboard-company-mini-card">
                                          <span className="hierarchy-dashboard-mini-label">요율</span>
                                          <span className="hierarchy-dashboard-mini-value hierarchy-dashboard-empty-inline">-</span>
                                        </div>
                                      )}
                                      <div className="hierarchy-dashboard-company-mini-card">
                                        <span className="hierarchy-dashboard-mini-label">입금 수수료 (건당)</span>
                                        <span className="hierarchy-dashboard-mini-value">{fees && keys ? formatCurrency(fees[keys.deposit] ?? 0) : '-'}</span>
                                      </div>
                                      <div className="hierarchy-dashboard-company-mini-card">
                                        <span className="hierarchy-dashboard-mini-label">출금 수수료 (건당)</span>
                                        <span className="hierarchy-dashboard-mini-value">{fees && keys ? formatCurrency(fees[keys.withdrawal] ?? 0) : '-'}</span>
                                      </div>
                                    </div>
                                    )}
                                    {periodData && (
                                      <div className="hierarchy-dashboard-company-period-cards">
                                        <div className="hierarchy-dashboard-company-period-card" style={{ gridColumn: 1, gridRow: 1 }}>
                                          <span className="hierarchy-dashboard-period-card-label">총 입금액</span>
                                          <span className="hierarchy-dashboard-period-card-value">{formatCurrency(periodData.deposit_total ?? 0)}</span>
                                        </div>
                                        <div className="hierarchy-dashboard-company-period-card" style={{ gridColumn: 1, gridRow: 2 }}>
                                          <span className="hierarchy-dashboard-period-card-label">총 정산액</span>
                                          <span className="hierarchy-dashboard-period-card-value">{formatCurrency(periodData.total_settlement ?? 0)}</span>
                                        </div>
                                        <div className="hierarchy-dashboard-company-period-card" style={{ gridColumn: 2, gridRow: 1 }}>
                                          <span className="hierarchy-dashboard-period-card-label">정산 완료 금액</span>
                                          <span className="hierarchy-dashboard-period-card-value">{formatCurrency(periodData.settled_amount ?? 0)}</span>
                                        </div>
                                        <div className="hierarchy-dashboard-company-period-card hierarchy-dashboard-period-card-unsettled" style={{ gridColumn: 2, gridRow: 2 }}>
                                          <span className="hierarchy-dashboard-period-card-label">미정산 금액</span>
                                          <span className="hierarchy-dashboard-period-card-value">{formatCurrency(periodData.unsettled_amount ?? 0)}</span>
                                        </div>
                                        <div className="hierarchy-dashboard-company-period-card" style={{ gridColumn: 3, gridRow: 1 }}>
                                          <span className="hierarchy-dashboard-period-card-label">월 이용료</span>
                                          <span className="hierarchy-dashboard-period-card-value">{formatCurrency(periodData.monthly_fee ?? 0)}</span>
                                        </div>
                                        <div className="hierarchy-dashboard-company-period-card" style={{ gridColumn: 3, gridRow: 2 }}>
                                          <span className="hierarchy-dashboard-period-card-label">일 평균 금액</span>
                                          <span className="hierarchy-dashboard-period-card-value">
                                            {(() => {
                                              const start = settlementSummary?.startDate;
                                              const end = settlementSummary?.endDate;
                                              const total = Number(periodData.total_settlement ?? 0);
                                              if (!start || !end || total === 0) return formatCurrency(0);
                                              const s = new Date(start);
                                              const e = new Date(end);
                                              const days = Math.max(1, Math.floor((e - s) / (24 * 60 * 60 * 1000)) + 1);
                                              return formatCurrency(Math.floor(total / days));
                                            })()}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="hierarchy-dashboard-recent">
                    <h4 className="hierarchy-dashboard-block-title">기간 내 미정산 내역</h4>
                    {(() => {
                      const unsettledItems = settlementSummary.unsettledItems || [];
                      if (unsettledItems.length === 0) {
                        return <div className="hierarchy-dashboard-empty-text">기간 내 미정산 내역이 없습니다.</div>;
                      }
                      const totalPages = Math.max(1, Math.ceil(unsettledItems.length / UNSETTLED_PAGE_SIZE));
                      const page = Math.min(Math.max(1, unsettledPage), totalPages);
                      const slice = unsettledItems.slice(
                        (page - 1) * UNSETTLED_PAGE_SIZE,
                        page * UNSETTLED_PAGE_SIZE
                      );
                      return (
                        <>
                          <div className="hierarchy-settlement-table-wrap">
                            <table className="hierarchy-settlement-table">
                              <thead>
                                <tr>
                                  <th>정산일</th>
                                  <th>분류</th>
                                  <th>상태</th>
                                  <th>미정산 금액</th>
                                </tr>
                              </thead>
                              <tbody>
                                {slice.map((s, i) => (
                                  <tr
                                    key={`${s.settlement_date}-${s.company || ''}-${(page - 1) * UNSETTLED_PAGE_SIZE + i}`}
                                    className="hierarchy-settlement-row-unsettled"
                                  >
                                    <td className="hierarchy-settlement-date">{s.settlement_date}</td>
                                    <td>{s.company || '-'}</td>
                                    <td>
                                      <span className="hierarchy-settlement-status-badge unsettled">미정산</span>
                                    </td>
                                    <td className="hierarchy-settlement-amount">{formatCurrency(s.unsettled_amount || 0)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="hierarchy-unsettled-pagination">
                            <button
                              type="button"
                              className="hierarchy-unsettled-page-btn"
                              disabled={page <= 1}
                              onClick={() => setUnsettledPage(page - 1)}
                            >
                              이전
                            </button>
                            <span className="hierarchy-unsettled-page-info">
                              {page} / {totalPages} 페이지 · 총 {unsettledItems.length}건
                            </span>
                            <button
                              type="button"
                              className="hierarchy-unsettled-page-btn"
                              disabled={page >= totalPages}
                              onClick={() => setUnsettledPage(page + 1)}
                            >
                              다음
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              ) : settlementSummary ? (
                <div className="hierarchy-settlement-empty">연결된 정산 분류가 없습니다.</div>
              ) : (
                <div className="hierarchy-detail-empty">정산 데이터가 없습니다.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="hierarchy-detail-empty">계보도에서 사용자를 선택하면 정보가 표시됩니다.</div>
        )}
      </div>
    </div>
  );
}

export default HierarchyView;
